import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LaunchIcon from '@mui/icons-material/Launch';
import classes from './classes.module.scss';
import MediaSwiper from 'app/components/MediaSwiper';
import PhotoSwipe from 'app/components/PhotoSwipe';


import {
  selectViewportMobile
} from 'app/reducers/viewportReducer';

import {
  cacheBustUrl
} from 'shared/utils/Utils';

const Gallery = props => {
  const {
    image,
    images,
    imageCount,
    imagesPerPage,
    getImages,
    psActions,
    isMobile,
    progressBar
  } = props;

  const [ready, setReady] = useState(false);
  useEffect(() => {
    // if a single image is passed with a lastUpdate field
    // then cache bust the URL
    if (image?.src && image?.lastUpdate) {
      image.src = cacheBustUrl(image.src, image.lastUpdate);
    }

    setReady(true);
  }, []);

  // if no images then just show the progress bar
  // (it'll be hidden unless active)
  if (!image && !imageCount) {
    return progressBar || null;
  }

  const style = {
    height: isMobile ? '250px' : '350px'
  }

  // it would be great to render JSX in a PhotoSwipe slide.
  // this seems to be almost working but not quite.
  // until that works, JSX can be rendered directly.
  // currently this will only work for a single image
  if (image?.jsx) {
    return (
      <div className={classes.photoSwipeContainer} style={style}>
        {image.jsx}
      </div>
    )
  }

  let showImages = ready && (image || imageCount || (images && images.length));

  let items = images || (image && [image]);

  // changing lastUpdate will refresh PhotoSwipe.
  // in theory this would be a Date.now() timestamp
  // but it can actually be anything,
  // it just triggers the refresh when it changes.
  // falling back to the image src to enable automatic
  // refresh when the src changes
  let lastUpdate = null;
  let firstImage = images ? images[0] : image;
  if (firstImage) {
    lastUpdate = firstImage.lastUpdate || firstImage.src;
  }
  lastUpdate ||= props.lastUpdate;

  return (
    <>
      <div className={classes.photoSwipeContainer} style={style}>
        {showImages && <PhotoSwipe
          items={items}
          itemCount={imageCount || 1}
          itemsPerPage={imagesPerPage}
          getItems={getImages}
          actions={psActions}
          lastUpdate={lastUpdate}
        />}
        {/* {showImages &&
              <MediaSwiper items={items} itemsPerPage={imagesPerPage} getItems={getImages} updateTimeStamp={Date.now()} />
            }}
        */}
      </div>
      {progressBar}
    </>
  );
}

const Details = props => {
  const {
    details,
    isMobile
  } = props;

  if (!details) {
    return null;
  }

  let tableRows = details.map(({ label, value }, i) => ((
    <TableRow key={'details-' + i}>
      <TableCell className={classes.th}>{label}</TableCell>
      <TableCell align="right">{value}</TableCell>
    </TableRow>
  )));

  // set table-layout: fixed for mobile, otherwise it'll overflow.
  // for desktop the auto setting is better
  let tableStyle = null;
  if (isMobile) {
    tableStyle = {
      tableLayout: 'fixed'
    }
  }

  let tableContainerStyle = null;
  if (isMobile) {
    tableContainerStyle = {
      paddingRight: '1rem',
      marginBottom: '1rem'
    }
  }

  return (
    <div style={tableContainerStyle}>
      <Table size="small" style={tableStyle}>
        <TableBody>
          {tableRows}
        </TableBody>
      </Table>
    </div>
  );
}

const List = props => {
  const {
    list
  } = props;

  if (!list) {
    return null;
  }

  let items = list.items.map(item => <li>{item}</li>);

  return (
    <div>
      <p className={classes.th}>{list.header}</p>
      <ul className={classes.list}>
        {items}
      </ul>
    </div>
  )
}

const Footnote = props => {
  const {
    footnote
  } = props;

  if (!footnote) {
    return null;
  }

  return <p className={classes.footnote}>{footnote}</p>;
}

const Buttons = props => {
  const {
    actions,
    isMobile
  } = props;

  if (!actions) {
    return null;
  }

  let size = 'large';
  let style = {};
  if (isMobile) {
    style.fontSize = '0.8rem';
  }
  style.marginTop = '0.5rem';
  style.marginBottom = '0.5rem';
  style.textTransform = 'none';
  style.boxShadow = 'none';
  style.background = '#f3f3f8'; // captura grey

  let buttons = actions.map(action => {
    let title = null;
    let newTabIcon = null;

    if (action.newTabLink) {
      title = 'Open new tab';
      newTabIcon = (
        <span className={classes.newTabLink}>
          <LaunchIcon />
        </span>
      )
    }

    return (
      <Button
        key={'action-' + action.label.replace(" ", "")}
        variant={action.variant || 'contained'}
        //color="secondary" // theme not working right
        className={classes.actionButton}
        onClick={action.callback}
        href={action.newTabLink}
        target="_blank"
        size={size}
        style={style}
        title={title}
      >
        {action.label}
        {newTabIcon}
      </Button>
    )
  })

  return <div className={classes.buttons}>{buttons}</div>;
}

const Message = props => {
  const {
    message
  } = props;

  if (!message) {
    return null;
  }

  return <div>{message}</div>;
}

const PanelCard = props => {
  const {
    image,
    images,
    getImages,
    imageCount,
    imagesPerPage,
    label,
    hideBorder,
    actions,
    message,
    details,
    list,
    isMobile,
    psActions,
    footnote,
    progressBar
  } = props;

  // if there are action buttons, then show them on the right.
  // otherwise leave as single column
  let grid;
  if (actions) {
    grid = (
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Message message={message} />
          <Details details={details} isMobile={isMobile} />
          <List list={list} />
          <Footnote footnote={footnote} />
        </Grid>
        <Grid item xs={6} className={classes.buttonsColumn}>
          <Buttons actions={actions} isMobile={isMobile} />
        </Grid>
      </Grid>
    );
  } else {
    grid = (
      <div>
        <Message message={message} />
        <Details details={details} isMobile={isMobile} />
        <List list={list} />
        <Footnote footnote={footnote} />
      </div>
    );
  }

  let contents = (
    <div>
      <span className={classes.cardLabel}>
        {label}
      </span>
      <Gallery
        image={image}
        images={images}
        imageCount={imageCount}
        imagesPerPage={imagesPerPage}
        getImages={getImages}
        psActions={psActions}
        isMobile={isMobile}
        progressBar={progressBar}
      />
      {grid}
    </div>
  );

  if (hideBorder) {
    let cardStyle = {
      padding: 0
    }

    return (
      <div className={classes.card} style={cardStyle}>
        {contents}
        <br />
        <hr />
      </div>
    )
  } else {
    return (
      <Card className={classes.card} variant="outlined">
        {contents}
      </Card>
    )
  }
}

const ResourceModalPanel = props => {
  const {
    visible,
    cards
  } = props;

  const isMobile = useSelector(state => state.viewportReducer.isMobile);

  // visible prop is optional, should default to true
  if (visible === false) {
    return null;
  }

  return (
    <div className={classes.container}>
      {cards.map((card, i) => <PanelCard {...card} key={'card-' + i} isMobile={isMobile} />)}
    </div>
  )
}

export default ResourceModalPanel;
